import { useEffect, useState } from "react";
import { Star, CheckCircle2, ChevronRight, Quote } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const REVIEW = {
  name: "review-name",
  email: "review-email",
  ratingStar: (n) => `review-rating-${n}`,
  comment: "review-comment",
  submit: "review-submit",
  successAlert: "review-success-alert",
};

const initial = { name: "", email: "", rating: 0, comment: "" };

export default function Reviews() {
  const [form, setForm] = useState(initial);
  const [hover, setHover] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const loadReviews = async () => {
    try {
      const res = await api.get("/review");
      setReviews(res.data || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadReviews(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rating) {
      toast.error("Please select a star rating");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/review", {
        name: form.name,
        email: form.email || null,
        rating: form.rating,
        comment: form.comment,
      });
      setSuccess(true);
      setForm(initial);
      toast.success("Thanks for your review!");
      loadReviews();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your details and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const avg = reviews.length
    ? (reviews.reduce((a, r) => a + (r.rating || 0), 0) / reviews.length).toFixed(1)
    : "—";

  return (
    <div className="bg-black text-white">
      <section className="py-24 md:py-32 border-b border-white/8 relative">
        <div className="absolute inset-0 grid-lines-navy opacity-30" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-condensed text-xs font-bold tracking-[0.28em] uppercase text-red-500 mb-3">— Reviews</p>
          <h1 className="font-display font-black text-4xl md:text-6xl uppercase leading-[1] text-balance max-w-3xl">
            What Our Clients<br />Say About Us<span className="text-red-500">.</span>
          </h1>
          <div className="mt-6 flex items-center gap-5 text-slate-400">
            <div className="flex items-center gap-2">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star key={n} className="w-5 h-5 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <span className="font-display font-black text-2xl text-white">{avg}</span>
            </div>
            <span className="font-condensed uppercase tracking-widest text-xs">
              Based on {reviews.length} {reviews.length === 1 ? "review" : "reviews"}
            </span>
          </div>

        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-10">
          {/* Form */}
          <div className="lg:col-span-2">
            <div className="bg-[#0A0A0A]/80 backdrop-blur-xl border border-white/10 rounded-sm p-6 sm:p-8">
              <p className="font-condensed text-xs font-bold tracking-[0.28em] uppercase text-red-500 mb-3">— Leave a Review</p>
              <h2 className="font-display font-black text-2xl uppercase mb-6">Share Your Experience</h2>
              {success ? (
                <div data-testid={REVIEW.successAlert} className="text-center py-6">
                  <div className="w-14 h-14 rounded-sm bg-red-600 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-display font-black text-xl uppercase">Thank You.</h3>
                  <p className="mt-3 text-slate-400">Your review has been received.</p>
                  <button
                    onClick={() => setSuccess(false)}
                    className="mt-6 font-condensed text-sm uppercase tracking-widest text-red-500 hover:text-red-400 border-b border-red-600"
                    data-testid="review-submit-another"
                  >
                    Leave Another Review
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5">
                  <Field label="Your Name" required>
                    <input
                      required
                      value={form.name}
                      onChange={upd("name")}
                      data-testid={REVIEW.name}
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Email (optional)">
                    <input
                      type="email"
                      value={form.email}
                      onChange={upd("email")}
                      data-testid={REVIEW.email}
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Rating" required>
                    <div className="flex items-center gap-1.5" onMouseLeave={() => setHover(0)}>
                      {[1, 2, 3, 4, 5].map((n) => {
                        const active = (hover || form.rating) >= n;
                        return (
                          <button
                            key={n}
                            type="button"
                            data-testid={REVIEW.ratingStar(n)}
                            onMouseEnter={() => setHover(n)}
                            onClick={() => setForm({ ...form, rating: n })}
                            className="p-1 transition-transform hover:scale-110"
                            aria-label={`${n} star${n > 1 ? "s" : ""}`}
                          >
                            <Star
                              className={cn(
                                "w-8 h-8 transition-colors",
                                active ? "fill-amber-400 text-amber-400" : "text-slate-600"
                              )}
                            />
                          </button>
                        );
                      })}
                      <span className="ml-2 font-condensed uppercase tracking-widest text-xs text-slate-400">
                        {form.rating ? `${form.rating}/5` : "Select rating"}
                      </span>
                    </div>
                  </Field>
                  <Field label="Comment" required>
                    <textarea
                      required
                      rows={5}
                      value={form.comment}
                      onChange={upd("comment")}
                      data-testid={REVIEW.comment}
                      className={cn(inputCls, "resize-none")}
                      placeholder="Tell us about your experience with Marco Polo Security…"
                    />
                  </Field>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid={REVIEW.submit}
                    className="group w-full bg-white hover:bg-slate-100 disabled:opacity-60 text-black px-6 py-3.5 rounded-sm font-condensed uppercase tracking-wider font-bold transition-colors flex items-center justify-center gap-2"
                  >
                    {submitting ? "Submitting…" : (<>Submit Review <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></>)}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Reviews list */}
          <div className="lg:col-span-3">
            <p className="font-condensed text-xs font-bold tracking-[0.28em] uppercase text-red-500 mb-3">— Customer Reviews</p>
            <h2 className="font-display font-black text-2xl uppercase mb-6">Recent Reviews</h2>
            {loading ? (
              <p className="text-slate-500 font-condensed uppercase tracking-widest text-sm">Loading…</p>
            ) : reviews.length === 0 ? (
              <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-8 text-center">
                <Quote className="w-8 h-8 text-red-500 mx-auto mb-3" />
                <p className="text-slate-400">Be the first to leave a review!</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="review-list">
                {reviews.map((r) => (
                  <div key={r.id} className="bg-[#0A0A0A] border border-white/10 rounded-sm p-6 hover:border-red-500/40 transition-colors">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div>
                        <p className="font-display font-black text-white uppercase">{r.name}</p>
                        <p className="font-condensed text-[10px] tracking-widest uppercase text-slate-500 mt-0.5">
                          {new Date(r.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                        </p>
                      </div>
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star
                            key={n}
                            className={cn(
                              "w-4 h-4",
                              (r.rating || 0) >= n ? "fill-amber-400 text-amber-400" : "text-slate-700"
                            )}
                          />
                        ))}
                      </div>
                    </div>
                    <p className="text-slate-300 leading-relaxed">{r.comment}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

const inputCls =
  "w-full bg-black border border-white/15 rounded-sm px-4 py-3 h-11 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block font-condensed text-xs font-bold uppercase tracking-widest text-slate-300 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </span>
      {children}
    </label>
  );
}
